<template>
  <div
    v-if="value"
    class="pt-4 equities-form"
  >
    <div class="row">
      <div
        class="col-12 col-md-8 border-right"
      >
        <div class="form-group">
          <label>Security</label>
          <multiselect
            :class="{
              'is-invalid': ((error && error.errors.tellimerSecurityId) ? error.errors.tellimerSecurityId : []).length > 0
            }"
            v-model="value.security"
            :options="securities"
            :internal-search="false"
            :allow-empty="false"
            :searchable="true"
            :multiple="false"
            :show-labels="false"
            class="multiselect"
            style="cursor:pointer"
            select-label="Select"
            track-by="id"
            label="security_name_BBG"
            @search-change="delayGetSecurities"
            @select="(val) => { value.tellimerSecurityId = val.id }"
          >
            <template v-slot:option="props">
              <strong>{{ props.option.security_name_BBG }}</strong> &mdash; {{ props.option.security_code_BBG }} &mdash; {{ props.option.security_code_ISIN }}
            </template>
          </multiselect>

          <form-control-error :errors="(error && error.errors.tellimerSecurityId) ? error.errors.tellimerSecurityId : []" />
        </div>

        <div class="form-group d-flex flex-column equities-form__side">
          <label>Side</label>
          <button
            class="btn btn-outline-secondary"
            :class="{'btn-outline-primary': value.side === 'BUY' }"
            @click="value.side = 'BUY'"
          >
            Buy
          </button>
          <button
            class="btn btn-outline-secondary"
            :class="{'btn-outline-primary': value.side === 'SELL' }"
            @click="value.side = 'SELL'"
          >
            Sell
          </button>
        </div>

        <span class="label">
          Client Exclusion List
        </span>
        <quick-selector
          :value="exclusionList"
          :options="clients"
          :selected="[]"
          :allow-creation="false"
          :async="true"
          :class="{
            'is-invalid': ((error && error.errors.tickers) ? error.errors.tickers : []).length > 0
          }"
          class="mt-1"
          search-key="ticker"
          placeholder="Add a ticker"
          @search-changed="delayGetClients"
        >
          <span
            slot="option"
            slot-scope="prop"
          >
            {{ prop.option.name_bbg }}
          </span>
        </quick-selector>
        <form-control-error
          :errors="(error && error.errors.tickers) ? error.errors.tickers : []"
        />
        <ul
          class="list-group list-group-flush list-group-sortable mb-3"
        >
          <li
            v-for="(client, index) in exclusionList"
            :key="client.id"
            style=""
            class="article__list-group-item list-group-item "
          >
            <div class="d-flex align-items-center justify-content-between">
              <div>
                {{ client.code_bbg }}
              </div>
              <div>
                <icon
                  @click.native="exclusionList.splice(index, 1)"
                  icon="remove-tag"
                  style="cursor: pointer;"
                  class="text-gray-600"
                />
              </div>
            </div>
          </li>
        </ul>

        <form-control
          title="Price"
          v-model="value.price"
        />

        <form-control
          title="Quantity"
          v-model="value.quantity"
        />
      </div>

      <div class="col-12 col-md-4">
        <div class="form-group d-flex flex-column">
          <label>Qualifier</label>
          <multiselect
            :class="{
              'is-invalid': ((error && error.errors.qualifier_id) ? error.errors.qualifier_id : []).length > 0
            }"
            v-model="value.qualifier"
            :options="qualifiers"
            :internal-search="true"
            :allow-empty="false"
            :searchable="true"
            :multiple="false"
            :show-labels="false"
            style="cursor:pointer"
            select-label="Select"
            track-by="id"
            label="short_id"
            @search-change="delayGetSecurities"
            @select="(val) => { value.qualifier_id = val.id }"
          >
            <template v-slot:option="props">
              <strong>{{ props.option.short_id }}</strong> &mdash; {{ props.option.name }}
            </template>
          </multiselect>

          <form-control-error :errors="(error && error.errors.qualifier_id) ? error.errors.qualifier_id : []" />
        </div>
        <div class="form-group">
          <form-control
            title="Qualifier Comments"
            v-model="value.qualifierComments"
          />
        </div>

        <div class="form-group border-bottom pb-3">
          <label>Natural</label>
          <multiselect
            v-model="natural"
            :options="[
              {
                name: 'Yes',
                value: true,
              },
              {
                name: 'No',
                value: false,
              }
            ]"
            :allow-empty="true"
            :searchable="false"
            :show-labels="false"
            label="name"
            track-by="value"
          />
        </div>

        <div class="form-group">
          <label>Expires</label>
          <multiselect
            v-model="expiryDate"
            :options="expiries"
            :allow-empty="true"
            :searchable="false"
            :show-labels="false"
            label="name"
            track-by="value"
            placeholder="Midnight tonight"
          />
        </div>
      </div>
    </div>
    <div class="d-flex py-3 w-100 border-top mt-3">
      <button
        class="btn btn-primary ml-auto "
        @click="action"
      >
        {{ edit ? 'Update': 'Create' }} IOI
      </button>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import QuickSelector from '@/components/QuickSelector'
import {debounce} from '@/helpers'
import algolia from '@/algolia'

export default {
  components: {
    QuickSelector,
  },
  props: {
    error: {
      required: false,
      type: Object,
      default: null,
    },
    data: {
      required: false,
      type: Object,
      default () {
        return Object.assign({}, {})
      },
    },
    edit: {
      required: false,
      type: Boolean,
      default: () => false,
    },
  },

  computed: {
    expiries () {
      const currentTime = parseInt(moment().format('HH'), 10) + 4

      return Array.from(new Array(24 - currentTime), (x, i) => {
        const m = moment().hour(i + currentTime).minute(0).seconds(0).millisecond(0)
        return {
          name: `Today @ ${m.format('HH:mm')}`,
          value: m.valueOf(),
        }
      })
    },
  },

  data () {
    return {
      expiryDate: null,
      expiryTime: null,
      qualifiers: [],
      selectedQualifier: null,
      securities: [],
      selectedSecurity: null,
      clients: [],
      exclusionList: [],
      value: null,
      natural: null,
    }
  },
  watch: {
    data: {
      deep: true,
      handler () {
        this.reset()
      },
    },
  },

  async mounted () {
    await this.fetchRequiredData()
    this.reset()

    // bind expiryDate/Time
    // if (this.value.expiry_date) {
    // this.expiryDate = moment(this.value.expiry_date).toDate()
    // this.expiryTime = moment.unix(this.value.expiry_date).format('HH:mm')
    // }
  },

  methods: {
    async reset () {
      this.value = {...this.data}
      if (this.value.exclude) {
        this.exclusionList = this.value.exclude.split(',').map((v) => {
          return {
            code_bbg: v,
          }
        })
      } else {
        this.exclusionList = []
      }

      if (this.value.qualifier_id) {
        this.$set(this.value, 'qualifier', this.qualifiers.find(({id}) =>
          this.value.qualifier_id === id
        ))
        this.qualifier_id = this.value.qualifier_id
      }

      if (this.value.securityNameBBG) {
        const [security = null] = await this.searchSecurities(this.value.securityNameBBG)
        this.$set(this.value, 'security', security)
        this.tellimerSecurityId = security.id
      }

      if (this.value.expiry_date) {
        const m = moment(this.value.expiry_date)
        this.expiryDate = {
          value: m.valueOf(),
          name: `Today @ ${m.format('HH:mm')}`,
        }
      }
    },

    delayGetClients: debounce(function (query) {
      algolia.search([{
        indexName: 'buySideClientsIndex',
        query,
        params: {
          hitsPerPage: 10,
        },
      }], (err, results) => {
        this.clients = results.results[0].hits
      })
    }, 200),
    delayGetSecurities: debounce(function (query) {
      algolia.search([{
        indexName: 'securityIndex',
        query,
        params: {
          hitsPerPage: 10,
        },
      }], (err, results) => {
        this.securities = results.results[0].hits
      })
    }, 200),
    async fetchRequiredData () {
      // get qualifiers
      try {
        const {data: qualifiers} = await this.$http.get('/iois/iois/qualifiers')
        this.qualifiers = qualifiers
      } catch (error) {
        this.qualifiers = []
      }
    },

    async searchSecurities (q = '') {
      // get qualifiers
      try {
        const {data: securities} = await this.$http.get(`/iois/securities/search/${q}`)
        this.securities = securities
      } catch (error) {
        this.securities = []
      }
      return this.securities
    },

    getDateTime () {
      const [hours, minutes] = this.expiryTime.split(':')
      return +moment(this.expiryDate)
          .hours(+hours)
          .minutes(+minutes)
    },

    selectQualifier ({object = null}) {
      this.selectedQualifier = object
    },

    clearQualifier () {},

    selectSecurity ({object = null}) {
      this.selectedSecurity = object
    },

    clearSecurity () {},

    action () {
      const {
        id,
        quantity,
        price,
        tellimerSecurityId,
        qualifier_id, // eslint-disable-line
        side,
      } = this.value

      this.$emit('action', {
        id,
        qualifier_id,
        tellimerSecurityId,
        firmId: this.$login.channelScopeId() || 1, // TODO: research
        userId: this.$login.getUser().id,
        entry_date: this.edit ? this.value.entry_date : moment().valueOf(),
        expiry_date: this.expiryDate ? this.expiryDate.value : moment().add(1, 'd').startOf('day').utc().valueOf(),
        asset_class_type_str: 'equities',
        quantity: +quantity,
        price: price,
        natural: this.natural ? this.natural.value : null,
        side,
        exclude: this.exclusionList.map((v) => v.code_bbg).join(','),
      })
    },
  },
}
</script>

<style lang="scss">
.equities-form {
  &__side {
    .btn {
      width: 150px;

      &:focus {
        background: unset;
        border-color: unset;
        box-shadow: none;
      }

      &.btn-outline-primary:hover {
        background: unset;
        border-color: $primary;
        span {
          color: $primary;
        }
      }
    }
  }
}
</style>

